import React, { useState, useEffect } from "react";
import { Table, notification, Tooltip, Tag } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { coreApi } from "../../api/calls";
import PartnerLink from "../Partners/PartnerLink";

const CityStatisticsGrouped = () => {
  const { t } = useTranslation();
  const [statisticsData, setStatisticsData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchGroupedCityStatistics = () => {
    setLoading(true);
    coreApi
      .get("/approval-payment/city-statistics-grouped")
      .then((res) => {
        const sortedData = res.data.sort((a, b) => a.city_branch.name.localeCompare(b.city_branch.name));
        setStatisticsData(sortedData);
      })
      .catch((err) => {
        notification.error({
          message: t("finance.transactions.loadError"),
          description: err.message,
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchGroupedCityStatistics();
  }, []);

  const hasValidationIssues = (record) => {
    return record?.validation_issues && Object.keys(record.validation_issues).length > 0;
  };

  const getValidationIssuesContent = (record) => {
    if (!hasValidationIssues(record)) return null;

    const validationIssues = record.validation_issues;
    const allPartners = [];

    Object.entries(validationIssues).forEach(([issueType, issueData]) => {
      if (issueData.partners) {
        allPartners.push(...issueData.partners);
      }
    });
    
    return (
      <div>
        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
          {t("finance.paymentsForApproval.partnerDetailsErrors")}
        </div>
        {allPartners.map((partner, index) => {
          const isPartnerPayment = partner.id !== null;
          console.log('partner:',partner);
          return (
            <div key={index} style={{ marginBottom: '4px', fontSize: '12px' }}>
              • {isPartnerPayment ? (
                <PartnerLink partner={{ id: partner.id, company_name: partner.name }} />
              ) : (
                partner.invoice_id ? (
                  <Link style={{ fontWeight: 500 }} to={"/invoices/" + partner.invoice_id} target={"_blank"}>
                    {partner.name}
                  </Link>
                ) : (
                  <span>{partner.name}</span>
                )
              )} - {new Intl.NumberFormat("cs-CZ", {
                style: "currency",
                currency: record?.city_branch?.currency || "CZK",
              }).format(partner.amount)}
              {partner.payment_id && (
                <span style={{ opacity: 0.7 }}> (Payment ID: {partner.payment_id})</span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const columns = [
    {
      title: t("finance.transactions.city"),
      dataIndex: ["city_branch", "name"],
      key: "cityBranchName",
      render: (cityName, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{cityName}</span>
          {hasValidationIssues(record) && (
            <Tooltip title={getValidationIssuesContent(record)}>
              <Tag color="warning" icon={<ExclamationCircleOutlined />}>
                {t("finance.paymentsForApproval.hasIssues")}
              </Tag>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: t("finance.transactions.totalPaymentsForApproval"),
      dataIndex: "totalPaymentsForApproval",
      key: "totalPaymentsForApproval",
      render: (value, record) => new Intl.NumberFormat("cs-CZ", {
        style: "currency",
        currency: record?.city_branch?.currency || "CZK",
      }).format(value),
    },
    {
      title: t("finance.transactions.totalRewardsForApproval"),
      dataIndex: "totalRewardsForApproval",
      key: "totalRewardsForApproval",
      render: (value, record) => new Intl.NumberFormat("cs-CZ", {
        style: "currency",
        currency: record?.city_branch?.currency || "CZK",
      }).format(value),
    },
    {
      title: t("finance.transactions.totalRewardsInReporting"),
      dataIndex: "totalRewardsInReporting",
      key: "totalRewardsInReporting",
      render: (value, record) => new Intl.NumberFormat("cs-CZ", {
        style: "currency",
        currency: record?.city_branch?.currency || "CZK",
      }).format(value),
    },
    {
      title: t("finance.transactions.rewardDifference"),
      dataIndex: "rewardDifference",
      key: "rewardDifference",
      render: (value, record) => new Intl.NumberFormat("cs-CZ", {
        style: "currency",
        currency: record?.city_branch?.currency || "CZK",
      }).format(value),
    },
    {
      title: t("finance.transactions.overpayments"),
      dataIndex: "overpayments",
      key: "overpayments",
      render: (value, record) => new Intl.NumberFormat("cs-CZ", {
        style: "currency",
        currency: record?.city_branch?.currency || "CZK",
      }).format(value),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={statisticsData}
      loading={loading}
      rowKey={(record) => record.city_branch?.id || Math.random()}
      pagination={false}
      size="small"
      scroll={{ y: 500 }}
    />
  );
};

export default CityStatisticsGrouped;
